import React, { useState } from 'react'
import { connect } from 'react-redux'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import {
  createEmailVerification, resendEmailVerification, verifyEmailVerification,
} from '../../../actions/emailVerification'
import { showNotification } from '../../../actions/notifications'
import useEmailVerificationRequest from '../../../utils/emailVerification/useEmailVerificationRequest'

// Simple, standalone "add/update a backup email" flow for the account page —
// just email (+ reauth if the session needs it) -> code -> done. This is
// intentionally separate from the bigger email-verification prompt modal.
// Adding and replacing a backup both use the same upsertBackup purpose (the
// existing backup stays live until the new address verifies) — this
// container just adapts its copy based on whether one is already set.
function AddBackupEmailContainer(props) {
  const {
    user, signinMethods, hideModal,
    createEmailVerification, resendEmailVerification, verifyEmailVerification,
    showNotification,
  } = props

  const isUpdating = !!user.backupEmail

  const [step, setStep] = useState('form') // 'form' | 'code'
  const {
    verification, requestCode, requestLoading, requestError, setRequestError,
    showReauth, handleReauthenticate, cancelReauth, submitCode, resend,
  } = useEmailVerificationRequest({
    userId: user.id,
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    onCodeSent: () => setStep('code'),
  })

  const handleSubmit = ({ email }) => {
    if (user.backupEmail && email.toLowerCase() === user.backupEmail.toLowerCase()) {
      setRequestError('Please enter a different email address than your current backup email address.')
      return
    }
    requestCode({ purpose: 'upsertBackup', email })
  }

  const handleVerifyCode = async (code) => {
    await submitCode(code)
    hideModal()
    showNotification('', isUpdating ? 'Backup email updated' : 'Backup email added', 'default')
  }

  if (step === 'code') {
    return (
      <EmailVerificationCodeEntry
        email={verification.email}
        title={isUpdating ? 'Verify your new backup email address' : 'Verify your backup email address'}
        description={isUpdating ? 'Please enter the code we just sent to your new backup email address' : 'Please enter the code we just sent to your email address'}
        resendAvailableAt={verification.resendAvailableAt}
        showIllustration={false}
        onSubmitCode={handleVerifyCode}
        onResend={resend}
      />
    )
  }

  return (
    <React.Fragment>
      <EmailVerificationEmailPasswordForm
        title={isUpdating ? 'Update backup email address' : 'Add a backup email address'}
        description={isUpdating ?
          "We'll send a code to your new email address to complete the update." :
          'A backup email address covers you if you ever lose access to your primary email address.'}
        showIllustration={false}
        emailLabel='Backup email address'
        submitLabel={isUpdating ? 'Update backup' : 'Send confirmation code'}
        currentEmail={user.email}
        duplicateEmailError='Please enter a different email address than your account email address.'
        submitLoading={requestLoading}
        errorMessage={requestError}
        onSubmit={handleSubmit}
      />
      {showReauth && (
        <ReauthenticationModal
          title={isUpdating ? 'Update Backup Email Address' : 'Add a Backup Email Address'}
          signinMethodsToDisplay={signinMethods}
          onAuthenticate={handleReauthenticate}
          hideModal={cancelReauth}
        />
      )}
    </React.Fragment>
  )
}

export default connect(
  (state) => ({ user: state.user, signinMethods: state.signinMethods }),
  {
    createEmailVerification, resendEmailVerification, verifyEmailVerification, showNotification,
  },
)(AddBackupEmailContainer)
